import { InputHTMLAttributes, forwardRef, useRef, useState, useCallback, useImperativeHandle } from 'react';
import { cn } from '@lib/utils/cn';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
  /**
   * Oculta el contador de caracteres aunque haya `maxLength`. Útil en
   * formularios densos (ej. wizards) donde el "0/150" bajo cada campo suma
   * espacio vertical sin aportar valor. No cambia el límite real del input,
   * solo el contador visible. Default `false` — no afecta ningún formulario
   * existente.
   */
  hideCounter?: boolean;
  'data-testid'?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, helperText, hideCounter = false, className, 'data-testid': testId, ...props }, ref) => {
    const maxLen = props.maxLength;
    const showCounter = !hideCounter && maxLen != null && maxLen > 0;
    const innerRef = useRef<HTMLInputElement>(null);
    const [internalCount, setInternalCount] = useState(0);

    useImperativeHandle(ref, () => innerRef.current!, []);

    // Use props.value when available (controlled), fallback to internal count (uncontrolled/RHF)
    const charCount = showCounter
      ? (typeof props.value === 'string' ? props.value.length
        : typeof props.value === 'number' ? String(props.value).length
        : internalCount)
      : 0;

    const handleInput = useCallback((e: React.FormEvent<HTMLInputElement>) => {
      if (showCounter) setInternalCount(e.currentTarget.value.length);
    }, [showCounter]);

    const nearLimit = showCounter && maxLen! - charCount < maxLen! * 0.1;

    return (
      <div className="w-full">
        {label && (
          <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
            {label}
            {props.required && <span className="text-danger-500 ml-1">*</span>}
          </label>
        )}
        <input
          ref={innerRef}
          data-testid={testId}
          onInput={handleInput}
          className={cn(
            'block w-full rounded-lg border-secondary-300 dark:border-secondary-600',
            'bg-white dark:bg-secondary-800',
            'text-secondary-900 dark:text-secondary-100',
            'placeholder-secondary-400 dark:placeholder-secondary-500',
            'focus:border-primary-500 focus:ring-primary-500',
            'disabled:bg-secondary-100 dark:disabled:bg-secondary-900 disabled:cursor-not-allowed',
            error && 'border-danger-500 focus:border-danger-500 focus:ring-danger-500',
            className
          )}
          {...props}
        />
        {(error || helperText || showCounter) && (
          <div className="flex justify-between mt-1">
            <div className="flex-1">
              {error && (
                <p className="text-sm text-danger-600 dark:text-danger-400">{error}</p>
              )}
              {helperText && !error && (
                <p className="text-sm text-secondary-500 dark:text-secondary-400">{helperText}</p>
              )}
            </div>
            {showCounter && (
              <span className={cn(
                'text-xs ml-2 flex-shrink-0',
                nearLimit
                  ? 'text-warning-600 dark:text-warning-400'
                  : 'text-secondary-400 dark:text-secondary-500'
              )}>
                {charCount}/{maxLen}
              </span>
            )}
          </div>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';
